import { Component, ElementRef, OnInit } from '@angular/core';

interface FeaturedImageBlockViewModel {
    ImageUrl?: string;
    Caption?: string;
}

@Component({
    selector: 'featured-image-block',
    templateUrl: './featured-image-block.component.html',
    styles: [`
        /* Crop to fill when the image is stretched to its sibling's height, instead of distorting it. */
        .featured-image-block-image {
            object-fit: cover;
        }

        .featured-image-block-caption {
            margin-top: 15px;
        }

        @media (max-width: 767.98px) {
            .featured-image-block-caption {
                margin-top: 20px;
            }
        }
    `],
})
export class FeaturedImageBlockComponent implements OnInit {
    public config: FeaturedImageBlockViewModel | null = null;
    public stretch = false;

    constructor(private host: ElementRef<HTMLElement>) { }

    ngOnInit(): void {
        // The server sends the block's data as JSON in an attribute; attributes survive Angular taking over the host element.
        const json = this.host.nativeElement.getAttribute('config');
        if (!json) {
            return;
        }

        try {
            this.config = JSON.parse(json) as FeaturedImageBlockViewModel;
        } catch {
            this.config = null;
        }

        this.stretch = this.isBesideOnlyMediaBlocks();
    }

    // Walk out to the first row where the image's column has neighbours. Only stretch when every
    // neighbour on the same line is a media block (directly or inside containers); next to anything
    // else (video, podcast, image...) the column grows with that content and would distort the image.
    // The featured text + image hero pair keeps stretching to the text panel's height.
    private isBesideOnlyMediaBlocks(): boolean {
        let column = this.host.nativeElement.parentElement;
        while (column && column.parentElement) {
            const row = column.parentElement;
            if (row.classList.contains('row') && row.children.length > 1) {
                const neighbours = FeaturedImageBlockComponent.lineOf(row, column).filter(child => child !== column);
                if (!neighbours.length) {
                    // Alone on its line of a wrapping row: nothing beside it to stretch to.
                    return false;
                }
                if (neighbours.some(neighbour => neighbour.querySelector('featured-text-block'))) {
                    return true;
                }
                return neighbours.every(neighbour => FeaturedImageBlockComponent.holdsOnlyMediaBlocks(neighbour));
            }
            column = row;
        }
        return false;
    }

    // A container puts all its items in one row, so items past 12 columns wrap onto further lines.
    // Rebuild the lines from the desktop column widths (col-md-N, else col-N, else full width), the
    // same way Bootstrap wraps them, and return the line holding the given column.
    private static lineOf(row: Element, column: Element): Element[] {
        let line: Element[] = [];
        let used = 0;
        for (const child of Array.from(row.children)) {
            const span = FeaturedImageBlockComponent.columnSpan(child);
            if (used + span > 12) {
                if (line.includes(column)) {
                    return line;
                }
                line = [];
                used = 0;
            }
            line.push(child);
            used += span;
        }
        return line.includes(column) ? line : [column];
    }

    private static columnSpan(column: Element): number {
        const match = /\bcol-md-(\d+)\b/.exec(column.className) || /\bcol-(\d+)\b/.exec(column.className);
        return match ? Math.min(+match[1], 12) : 12;
    }

    private static holdsOnlyMediaBlocks(column: Element): boolean {
        // Podcast cards share the media-block-item class for spacing, so exclude them explicitly.
        if (column.matches('.media-block-item:not(.podcast-block-item)')) {
            return true;
        }
        if (column.matches('.container-block-item')) {
            const items = Array.from(column.querySelectorAll(':scope > .container-block > .row > *'));
            return items.length > 0 && items.every(item => FeaturedImageBlockComponent.holdsOnlyMediaBlocks(item));
        }
        return false;
    }
}
